/**
 * Shared Firebase Web SDK client. Used by every monorepo app that authenticates
 * users (brand, admin). Reads VITE_FIREBASE_* env vars at consumer build time.
 *
 * The Go backend verifies Firebase ID tokens for ALL roles via
 * `internal/middleware/auth.go:RequireAuth`. Role enforcement (admin vs brand
 * vs creator) is per-route via `RequireRole`. So every FE app uses the same
 * Firebase project, just signs in different users.
 */
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  browserLocalPersistence,
  setPersistence,
  type Auth,
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';

// Exported so callers that need a throwaway, non-primary Firebase App
// instance (e.g. AccountSection's Google-link flow — obtaining a Google
// id_token without touching the primary signed-in session) can initialize
// one with the same project config.
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
];

const missingVars = requiredEnvVars.filter(
  (varName) => !import.meta.env[varName],
);

if (missingVars.length > 0 && import.meta.env.DEV) {
  console.error('Missing Firebase environment variables:', missingVars);
  console.error(
    'Please create a .env file with the required Firebase configuration.',
  );
}

let app!: FirebaseApp;
let auth!: Auth;
let db!: Firestore;

try {
  app = (
    getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]
  ) as FirebaseApp;
  auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.error('Failed to set Firebase persistence:', err);
  });
  db = getFirestore(app);
} catch (error) {
  console.error('Firebase initialization error:', error);
  if (import.meta.env.DEV) {
    console.error(
      'Firebase failed to initialize. Check your .env file and Firebase configuration.',
    );
  }
  throw error;
}

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export { app, auth, db };
